import { useMemo, useState } from "react";

import { MedicamentoCombobox } from "@/components/MedicamentoCombobox";
import { RoleGate } from "@/components/RoleGate";
import { Scanner } from "@/components/Scanner";
import {
  lookupLotePorEtiqueta,
  lookupMedicamentoPorEan,
  useAtenderItemRequisicao,
  useCancelarRequisicao,
  useCriarRequisicao,
  useFefoSugestao,
  useLocais,
  useLotesEmEstoque,
  useMedicamentos,
  useRequisicoes,
} from "@/hooks/useApi";
import { useUser } from "@/hooks/useUser";
import type { Local, Medicamento, Requisicao, RequisicaoItem, StatusRequisicao } from "@/types/api";

const STATUS_LABEL: Record<StatusRequisicao, string> = {
  solicitada: "Solicitada",
  aprovada: "Aprovada (aguarda farmácia)",
  atendida: "Atendida",
  recebida: "Recebida",
  cancelada: "Cancelada",
};

const STATUS_CLASS: Record<StatusRequisicao, string> = {
  solicitada: "badge-ordinario",
  aprovada: "badge bg-amber-100 text-amber-800",
  atendida: "badge bg-green-100 text-green-800",
  recebida: "badge bg-blue-100 text-blue-800",
  cancelada: "badge bg-gray-100 text-gray-500 line-through",
};

function fmtDate(s: string | null | undefined): string {
  if (!s) return "—";
  return new Date(s).toLocaleString("pt-BR", { timeZone: "America/Sao_Paulo" });
}

export function RequisicoesPage() {
  return (
    <RoleGate allowed={["admin", "auditor", "compras", "farmaceutico", "enfermeira_chefe", "enfermagem"]}>
      <Inner />
    </RoleGate>
  );
}

function Inner() {
  const { data: user } = useUser();
  const { data: locais } = useLocais();
  const { data: reqs, isLoading } = useRequisicoes();
  const [showNova, setShowNova] = useState(false);

  const podeCriar = user && ["admin", "enfermeira_chefe", "enfermagem"].includes(user.papel);

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="font-display text-2xl font-semibold">Requisições</h2>
          <p className="text-sm text-ink-muted">
            Movimentação farmácia → enfermagem. Sistema sugere o lote por FEFO
            (menor validade primeiro).
          </p>
        </div>
        {podeCriar ? (
          <button className="btn-primary" onClick={() => setShowNova((v) => !v)}>
            {showNova ? "Fechar" : "Nova requisição"}
          </button>
        ) : null}
      </div>

      {showNova && locais ? (
        <NovaRequisicao locais={locais} onDone={() => setShowNova(false)} />
      ) : null}

      {isLoading ? <p className="text-ink-muted">Carregando…</p> : null}
      {reqs && reqs.length > 0 ? (
        <div className="space-y-3">
          {reqs.map((r) => (
            <RequisicaoCard key={r.id} req={r} />
          ))}
        </div>
      ) : reqs && reqs.length === 0 ? (
        <p className="text-sm text-ink-muted">Sem requisições.</p>
      ) : null}
    </div>
  );
}

// ---------- Card ----------
function RequisicaoCard({ req }: { req: Requisicao }) {
  const { data: user } = useUser();
  const cancelar = useCancelarRequisicao();
  const [atendendoItem, setAtendendoItem] = useState<RequisicaoItem | null>(null);
  const podeAtender =
    user && ["admin", "farmaceutico"].includes(user.papel) &&
    ["solicitada", "aprovada"].includes(req.status);
  const podeCancelar =
    user &&
    ["solicitada", "aprovada"].includes(req.status) &&
    (user.papel === "admin" ||
      user.papel === "farmaceutico" ||
      user.id === req.solicitante_id);

  return (
    <div className="card p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <b className="font-mono text-sm">{req.numero}</b>
            <span className={STATUS_CLASS[req.status]}>{STATUS_LABEL[req.status]}</span>
          </div>
          <p className="text-sm text-ink-muted mt-1">
            Solicitado por <span className="font-medium">{req.solicitante_nome}</span>{" "}
            (<span className="font-medium">{req.destino.descricao}</span>) →{" "}
            atende <span className="font-medium">{req.origem.descricao}</span> · {fmtDate(req.created_at)}
          </p>
          {req.observacao ? (
            <p className="text-sm text-ink-muted mt-1">📝 {req.observacao}</p>
          ) : null}
        </div>
        <div className="flex gap-2">
          {podeCancelar ? (
            <button
              className="btn-secondary"
              disabled={cancelar.isPending}
              onClick={() => cancelar.mutate(req.id)}
            >
              Cancelar
            </button>
          ) : null}
        </div>
      </div>
      <div className="mt-3 space-y-1.5">
        {req.itens.map((it) => {
          const completo = it.qtd_atendida >= it.qtd_solicitada;
          const parcial = it.qtd_atendida > 0 && !completo;
          return (
            <div
              key={it.id}
              className="flex flex-wrap items-center justify-between gap-2 rounded bg-gray-50 px-3 py-1.5 text-sm"
            >
              <span>
                <code className="text-xs">{it.medicamento_codigo}</code> {it.medicamento_descricao}
                {completo ? <span className="badge ml-2 bg-green-100 text-green-800">✅ Atendido</span> : null}
                {parcial ? <span className="badge ml-2 bg-amber-100 text-amber-800">⏳ Parcial</span> : null}
                {it.observacao ? <span className="ml-2 text-xs text-ink-muted">📝 {it.observacao}</span> : null}
              </span>
              <span className="flex items-center gap-3 text-xs text-ink-muted">
                <span>
                  {parcial || completo
                    ? <>enviado <b>{it.qtd_atendida}</b> de <b>{it.qtd_solicitada}</b></>
                    : <>qtd <b>{it.qtd_solicitada}</b></>}
                </span>
                {podeAtender && !completo ? (
                  <button className="btn-secondary px-2 py-1 text-xs" onClick={() => setAtendendoItem(it)}>
                    Atender
                  </button>
                ) : null}
              </span>
            </div>
          );
        })}
      </div>
      {atendendoItem ? (
        <AtenderItemModal
          reqId={req.id}
          origemId={req.origem.id}
          item={atendendoItem}
          onClose={() => setAtendendoItem(null)}
        />
      ) : null}
    </div>
  );
}

// ---------- Form nova requisição ----------
function AtenderItemModal({ reqId, origemId, item, onClose }: {
  reqId: string; origemId: string; item: RequisicaoItem; onClose: () => void;
}) {
  const atender = useAtenderItemRequisicao();
  const { data: lotes } = useLotesEmEstoque(origemId, item.medicamento_id);
  const restante = item.qtd_solicitada - item.qtd_atendida;
  const [qtd, setQtd] = useState(String(restante));
  const [numeroLote, setNumeroLote] = useState("");
  const [validade, setValidade] = useState("");
  const [obs, setObs] = useState("");

  const loteExiste = (lotes ?? []).some((l) => l.numero_lote === numeroLote.trim());
  const precisaValidade = numeroLote.trim() !== "" && !loteExiste;
  const ok = Number(qtd) > 0 && Number(qtd) <= restante && numeroLote.trim() !== "" &&
    (!precisaValidade || !!validade);

  async function confirmar() {
    if (!ok) return;
    try {
      await atender.mutateAsync({
        reqId, itemId: item.id, qtd: Number(qtd),
        numero_lote: numeroLote.trim(),
        data_validade: validade || null,
        observacao: obs.trim() || null,
      });
      onClose();
    } catch { /* erro via atender.error */ }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="card w-full max-w-md p-4 space-y-3" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between">
          <h3 className="font-display text-lg font-semibold">Atender item</h3>
          <button className="text-ink-muted hover:text-ink" onClick={onClose}>✕</button>
        </div>
        <div className="rounded bg-gray-50 px-3 py-2 text-sm">
          <code className="text-xs">{item.medicamento_codigo}</code> {item.medicamento_descricao}
          <p className="text-xs text-ink-muted mt-0.5">Pendente: <b>{restante}</b> de {item.qtd_solicitada}</p>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs uppercase tracking-wider text-ink-muted">Qtd a enviar</label>
            <input className="input mt-1" type="number" min={1} max={restante} value={qtd}
              onChange={(e) => setQtd(e.target.value)} />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-ink-muted">Lote enviado</label>
            <input className="input mt-1" list={`lotes-${item.id}`} placeholder="digite ou escolha"
              value={numeroLote} onChange={(e) => setNumeroLote(e.target.value)} />
            <datalist id={`lotes-${item.id}`}>
              {(lotes ?? []).map((l) => (
                <option key={l.lote_id} value={l.numero_lote}>
                  {l.numero_lote} — val {l.data_validade.split("-").reverse().join("/")} · {l.quantidade} disp
                </option>
              ))}
            </datalist>
          </div>
        </div>

        {precisaValidade ? (
          <div>
            <label className="text-xs uppercase tracking-wider text-amber-800">Validade (lote novo, não está no estoque)</label>
            <input className="input mt-1" type="date" value={validade} onChange={(e) => setValidade(e.target.value)} />
          </div>
        ) : null}

        <div>
          <label className="text-xs uppercase tracking-wider text-ink-muted">Observação (opc.)</label>
          <input className="input mt-1" placeholder="ex.: enviado lote específico p/ carrinho de parada"
            value={obs} onChange={(e) => setObs(e.target.value)} />
        </div>

        <p className="text-xs text-ink-subtle">
          Enviando <b>{qtd || 0}</b> de {restante} → {Number(qtd) < restante ? "atendimento parcial (o resto fica pendente)" : "completa o item"}.
        </p>

        <div className="flex items-center justify-end gap-2 pt-1">
          <button className="btn-secondary" onClick={onClose}>Cancelar</button>
          <button className="btn-primary" disabled={!ok || atender.isPending} onClick={confirmar}>
            {atender.isPending ? "Enviando…" : "Confirmar envio"}
          </button>
        </div>
        {atender.error ? <p className="text-sm text-anarosa">{(atender.error as Error).message}</p> : null}
      </div>
    </div>
  );
}

interface NovaItemRow {
  key: string;
  medicamento_id: string;
  qtd: string;
}

function NovaRequisicao({ locais, onDone }: { locais: Local[]; onDone: () => void }) {
  const farmacias = useMemo(() => locais.filter((l) => l.tipo === "farmacia"), [locais]);
  const enfermagens = useMemo(() => locais.filter((l) => l.tipo === "enfermagem"), [locais]);
  const [origem, setOrigem] = useState(farmacias[0]?.id ?? "");
  const [destino, setDestino] = useState(enfermagens[0]?.id ?? "");
  const [observacao, setObservacao] = useState("");
  const [itens, setItens] = useState<NovaItemRow[]>([
    { key: crypto.randomUUID(), medicamento_id: "", qtd: "1" },
  ]);
  const { data: medicamentos } = useMedicamentos({});
  const criar = useCriarRequisicao();

  function addItem() {
    setItens((arr) => [...arr, { key: crypto.randomUUID(), medicamento_id: "", qtd: "1" }]);
  }
  function removeItem(key: string) {
    setItens((arr) => arr.filter((i) => i.key !== key));
  }
  function updateItem(key: string, patch: Partial<NovaItemRow>) {
    setItens((arr) => arr.map((i) => (i.key === key ? { ...i, ...patch } : i)));
  }

  async function submit() {
    const body = {
      origem_local_id: origem,
      destino_local_id: destino,
      observacao: observacao || undefined,
      itens: itens
        .filter((i) => i.medicamento_id && Number(i.qtd) > 0)
        .map((i) => ({
          medicamento_id: i.medicamento_id,
          qtd_solicitada: Number(i.qtd),
        })),
    };
    try {
      await criar.mutateAsync(body);
      onDone();
    } catch {
      // exibe via criar.error
    }
  }

  return (
    <div className="card p-4 space-y-4">
      <h3 className="font-display text-base font-semibold">Nova requisição</h3>
      <div className="grid grid-cols-2 gap-3">
        <select className="input" value={origem} onChange={(e) => setOrigem(e.target.value)}>
          {farmacias.map((l) => (
            <option key={l.id} value={l.id}>Origem: {l.descricao}</option>
          ))}
        </select>
        <select className="input" value={destino} onChange={(e) => setDestino(e.target.value)}>
          {enfermagens.map((l) => (
            <option key={l.id} value={l.id}>Destino: {l.descricao}</option>
          ))}
        </select>
      </div>

      <div className="space-y-2">
        {itens.map((row) => (
          <ItemRow
            key={row.key}
            row={row}
            origem={origem}
            medicamentos={medicamentos ?? []}
            onChange={(p) => updateItem(row.key, p)}
            onRemove={itens.length > 1 ? () => removeItem(row.key) : undefined}
          />
        ))}
        <button className="text-anarosa text-sm hover:underline" onClick={addItem}>
          + Adicionar item
        </button>
      </div>

      <textarea
        className="input"
        rows={2}
        placeholder="Observação (opcional)"
        value={observacao}
        onChange={(e) => setObservacao(e.target.value)}
      />

      <div className="flex items-center gap-3">
        <button
          className="btn-primary"
          disabled={!origem || !destino || criar.isPending}
          onClick={submit}
        >
          {criar.isPending ? "Salvando…" : "Criar requisição"}
        </button>
        {criar.error ? (
          <span className="text-anarosa text-sm">{(criar.error as Error).message}</span>
        ) : null}
      </div>
    </div>
  );
}

function ItemRow({
  row, origem, medicamentos, onChange, onRemove,
}: {
  row: NovaItemRow;
  origem: string;
  medicamentos: Medicamento[];
  onChange: (p: Partial<NovaItemRow>) => void;
  onRemove?: () => void;
}) {
  const fefo = useFefoSugestao(row.medicamento_id || null, origem || null);
  const [scannerOpen, setScannerOpen] = useState(false);
  const [scanErr, setScanErr] = useState<string | null>(null);

  async function handleScan(codigo: string) {
    setScannerOpen(false);
    setScanErr(null);
    try {
      const lote = await lookupLotePorEtiqueta(codigo);
      onChange({ medicamento_id: lote.medicamento_id });
      return;
    } catch { /* tenta EAN */ }
    try {
      const m = await lookupMedicamentoPorEan(codigo);
      onChange({ medicamento_id: m.id });
    } catch {
      setScanErr(`Código "${codigo}" não corresponde a EAN nem etiqueta interna.`);
    }
  }

  return (
    <div className="rounded-md border border-gray-200 p-3">
      <div className="grid grid-cols-1 gap-2 md:grid-cols-[1fr_120px_auto_auto]">
        <MedicamentoCombobox
          medicamentos={medicamentos}
          value={row.medicamento_id}
          onChange={(id) => onChange({ medicamento_id: id })}
        />
        <input
          className="input"
          type="number"
          min={1}
          value={row.qtd}
          placeholder="Qtd"
          onChange={(e) => onChange({ qtd: e.target.value })}
        />
        <button
          type="button"
          className="btn-secondary"
          title="Escanear EAN ou etiqueta interna"
          onClick={() => setScannerOpen(true)}
        >
          📷
        </button>
        {onRemove ? (
          <button className="text-ink-muted hover:text-anarosa text-sm" onClick={onRemove}>
            Remover
          </button>
        ) : <div />}
      </div>
      <Scanner open={scannerOpen} onScan={handleScan} onClose={() => setScannerOpen(false)} />
      {scanErr ? <p className="mt-1 text-xs text-anarosa">{scanErr}</p> : null}
      {row.medicamento_id && origem ? (
        <p className="mt-2 text-xs text-ink-muted">
          {fefo.isLoading
            ? "Buscando lote FEFO…"
            : fefo.isError
              ? "Sem estoque na farmácia agora — pode pedir; fica pendente até a farmácia repor e atender."
              : fefo.data
                ? <>FEFO sugerido: lote <b>{fefo.data.numero_lote}</b> · validade {fefo.data.data_validade.split("-").reverse().join("/")} · disponível: <b>{fefo.data.saldo_disponivel}</b></>
                : null}
        </p>
      ) : null}
    </div>
  );
}
